/**
 * "Has the question this visitor is waiting on actually been answered?"
 *
 * Both the socket layer (iframe.ts, deciding whether to stop watching for a
 * lost answer) and the UI layer (snippet.ts, deciding whether to stop the
 * caret and let pills back in) have to answer this on every chat:init, and
 * they must answer it the SAME way — two copies drifted apart once already.
 *
 * The tempting shortcut — "the history ends on an agent message" — is wrong,
 * and wrong in the exact way that produced the 2026-07-30 nemesis: a turn is
 * persisted only when it COMPLETES, so mid-turn the stored history ends on the
 * PREVIOUS turn's answer. Reading that as "answered" abandons the visitor's
 * real wait: caret off, pills back, question still unanswered.
 */
export type TranscriptMsg = { sender: string; text: string };

/**
 * True when `ask` appears in `messages` with an agent reply after it.
 * With no pending ask, falls back to "the history ends on an answer" — the
 * right question when nothing specific is owed.
 */
export function historyAnswersAsk(messages: TranscriptMsg[], ask: string | null): boolean {
  if (!ask) return messages[messages.length - 1]?.sender === "agent";
  const norm = (s: string) => (s ?? "").trim();
  const target = norm(ask);
  let idx = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]!;
    if (m.sender === "client" && norm(m.text) === target) {
      idx = i;
      break;
    }
  }
  if (idx < 0) return false; // not even in the stored history yet
  // An answer is a message with WORDS in it. Chrome is not an answer.
  //
  // chat:init carries text-less `sender:"agent"` rows that exist only to
  // smuggle answerChips through: chips travel live as an `answer_chips`
  // EventCommand, commands are not persisted, and the message list is the
  // only thing that survives a reconnect — so a fake message carries them
  // (widget-api/src/handler/chat.ts chip replay, now sourced from
  // chat_nudge_sets). Counting one
  // as an answer kills the caret and lets pills paint over a question that is
  // still unanswered — the exact bug this predicate exists to prevent.
  //
  // Today it cannot fire, because that replay only appends chips when the
  // last real row is already an assistant message. That guard lives in a
  // different file for a different reason, so this does not lean on it.
  return messages.slice(idx + 1).some((m) => m.sender === "agent" && m.text.trim() !== "");
}

/**
 * Id prefix for the LOCAL placeholder row the client paints for a question the
 * server has not persisted yet (#514). It is a stand-in for a real message, so
 * when the authoritative copy arrives it must REPLACE this row rather than sit
 * next to it — see adoptsPendingRow.
 */
export const PENDING_ID_PREFIX = "pending-";

/** Deterministic placeholder id for a send with a known correlation id. The
 *  echo carries the same clientMsgId, so adoption is exact — no text involved. */
export function placeholderIdFor(clientMsgId: string): string {
  return `${PENDING_ID_PREFIX}${clientMsgId}`;
}

/** True when `existing` is the local placeholder for the message `incoming`. */
export function adoptsPendingRow(
  existing: { id: string; sender: string; text?: string | null },
  incoming: { sender: string; text?: string | null },
): boolean {
  return (
    existing.id.startsWith(PENDING_ID_PREFIX) &&
    existing.sender === "client" &&
    incoming.sender === "client" &&
    (existing.text ?? "").trim() === (incoming.text ?? "").trim() &&
    (incoming.text ?? "").trim().length > 0
  );
}
